import React, { useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Crown, Users } from 'lucide-react';

interface CommunityTabsProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  communityId: string;
  showTabs?: boolean;
}

const CommunityTabs = ({
  activeTab,
  onTabChange,
  communityId,
  showTabs = false
}: CommunityTabsProps) => {
  
  // Query to get post counts for each tab
  const { data: postCounts } = useQuery({
    queryKey: ['communityPostCounts', communityId],
    queryFn: async () => {
      // Get all loop IDs for this community
      const { data: communityLoops, error: communityError } = await supabase
        .from('loop_communities')
        .select('loop_id')
        .eq('community_id', communityId);

      if (communityError) throw communityError;
      if (!communityLoops?.length) {
        return { admin: 0, community: 0 };
      }

      const loopIds = communityLoops.map(item => item.loop_id);

      // Get loops with user profiles to check admin status
      const { data: loopsData, error: loopsError } = await supabase
        .from('loops')
        .select(`
          id,
          user_id,
          profiles!inner(is_admin)
        `)
        .in('id', loopIds)
        .eq('is_active', true);

      if (loopsError) throw loopsError;

      const adminCount = loopsData?.filter(loop => {
        // Handle the profiles array properly
        const profile = Array.isArray(loop.profiles) ? loop.profiles[0] : loop.profiles;
        return profile?.is_admin === true;
      }).length || 0;
      
      const communityCount = loopsData?.filter(loop => {
        // Handle the profiles array properly
        const profile = Array.isArray(loop.profiles) ? loop.profiles[0] : loop.profiles;
        return profile?.is_admin !== true;
      }).length || 0;

      return {
        admin: adminCount,
        community: communityCount
      };
    },
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    enabled: !!communityId && showTabs
  });

  const handleTabChange = useCallback((value: string) => {
    onTabChange(value);
  }, [onTabChange]);
  
  const formatCount = (count: number) => {
    return count > 99 ? '99+' : count.toString();
  };

  if (!showTabs) {
    return null;
  }
  
  return (
    <div className="flex flex-col gap-2 mb-4">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-[#1A1F2C]/50 backdrop-blur-sm">
          <TabsTrigger 
            value="admin" 
            className="data-[state=active]:bg-[#f59e0b]/20 relative text-[#f59e0b] flex items-center gap-2"
          >
            <Crown size={16} />
            Admin Only
            {postCounts?.admin ? (
              <Badge variant="secondary" className="ml-2 min-w-[20px] h-5 flex items-center justify-center text-xs bg-[#f59e0b]/20 text-[#f59e0b]">
                {formatCount(postCounts.admin)}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger 
            value="community" 
            className="data-[state=active]:bg-[#10b981]/20 relative text-[#10b981] flex items-center gap-2"
          >
            <Users size={16} />
            Community
            {postCounts?.community ? (
              <Badge variant="secondary" className="ml-2 min-w-[20px] h-5 flex items-center justify-center text-xs bg-[#10b981]/20 text-[#10b981]">
                {formatCount(postCounts.community)}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
};

export default CommunityTabs;